/**
 * Centralised tier-gating logic.
 *
 * Tier hierarchy: basic < pro < ai
 * A user's effective tier comes from `profiles.tier`.
 * Admin users (adminMode=true) bypass every gate.
 *
 * ⚠️ Pricing display MUST come from Supabase (get_visible_pricing / products table).
 *    Do NOT hardcode prices here.
 */

export type AppTier = "basic" | "pro" | "ai";

const TIER_RANK: Record<AppTier, number> = { basic: 0, pro: 1, ai: 2 };

/** Returns true when the user's tier is high enough to access `requiredTier`. */
export function hasAccess(
  userTier: AppTier | string | null | undefined,
  requiredTier: AppTier | string,
  adminMode = false,
): boolean {
  if (adminMode) return true;
  const uRank = TIER_RANK[(userTier ?? "basic") as AppTier] ?? 0;
  const rRank = TIER_RANK[requiredTier as AppTier] ?? 0;
  return uRank >= rRank;
}

/**
 * Unified access check for any paid feature.
 */
export function canAccessFeature(opts: {
  adminMode: boolean;
  userTier: string;
  requiredTier?: string;
  skuKey?: string;
  hasPurchased: (sku: string) => boolean;
}): boolean {
  if (opts.adminMode) return true;
  if (opts.requiredTier && hasAccess(opts.userTier, opts.requiredTier)) return true;
  if (opts.skuKey && opts.hasPurchased(opts.skuKey)) return true;
  return false;
}

/** Human-readable label for the minimum tier needed to unlock a feature. */
export function upgradeTierLabel(requiredTier: AppTier | string): string {
  switch (requiredTier) {
    case "ai":
      return "AI";
    case "pro":
      return "Pro";
    default:
      return "Basic";
  }
}

/**
 * @deprecated — Pricing must come from Supabase products table.
 * Use useSubscriptionProducts().getByTier(tier)?.price instead.
 * Kept only as UI fallback label; do NOT use for checkout logic.
 */
export function upgradePrice(requiredTier: AppTier | string): string {
  switch (requiredTier) {
    case "ai":
      return "AI Plan";
    case "pro":
      return "Pro Plan";
    default:
      return "Basic Plan";
  }
}
